/**
 * Texts (`en`) for the supplier technical sheets, phase 1 (OBR-41): attaching them, requesting them and the
 * «Pending technical sheets by supplier» view. The Catalog's own slice: only this file is edited (the wiring is in
 * `cataleg/en.ts`). The three languages must have the same keys (i18n.test.ts); no literal text in the views.
 *
 * `emailSubjectDefault` and `emailBodyDefault` are the email TEMPLATES: they are rendered in the supplier's language
 * (the one used for purchase orders, or the requester's if none) and the person can edit them before sending.
 */

export const ftRequestEn = {
  'backoffice.ftRequest': {
    // ── Status ───────────────────────────────────────────────────────────────────────────
    statusNone: 'No tech sheet',
    statusRequested: 'Requested',
    statusValid: 'Valid',

    // ── The block of an article-supplier relation ────────────────────────────────────────
    blockTitle: 'Supplier technical sheet',
    noneHint: 'This supplier’s technical sheet has not been attached or requested yet.',
    requestedSummary: 'Requested on {{date}} from {{recipient}}.',
    requestedSummaryNoRecipient: 'Requested on {{date}}.',
    validDocument: 'Document dated {{date}} · version {{version}}',
    validAttached: 'Added on {{date}} by {{name}}.',
    validAttachedNoName: 'Added on {{date}}.',
    legacyHint:
      'Attached before its date and version were recorded: they are unknown. Attach it again or fill them in to keep a record.',
    openFile: 'Open the sheet',
    attachAction: 'Attach tech sheet',
    replaceAction: 'Replace tech sheet',
    completeAction: 'Fill in date and version',
    requestAction: 'Request tech sheet',
    requestAgainAction: 'Request again',
    saveFirstHint: 'Save the article to attach or request this supplier’s technical sheet.',
    supplierChangedHint:
      'You changed the supplier of this relation: save the article before attaching or requesting the technical sheet.',
    supplierInactiveHint: 'This supplier is deactivated: its technical sheet cannot be requested.',
    loadError: 'The technical sheet status of this article could not be read.',

    // ── Attach ───────────────────────────────────────────────────────────────────────────
    attachTitle: 'Attach the technical sheet of {{supplier}}',
    attachIntro:
      'The supplier’s technical sheet for {{article}}. The date printed on the document and its version are required.',
    fieldFile: 'File (PDF)',
    fieldFileHint: 'PDF up to 10 MB.',
    keepFile: 'Keep the current file and only correct the date and version',
    fieldDocumentDate: 'Document date',
    fieldDocumentDateHint: 'The one on the supplier’s sheet, not today’s.',
    fieldVersion: 'Version',
    fieldVersionHint: 'As the supplier states it (e.g. “v3” or “2026-05”).',
    attachConfirm: 'Attach',
    attachError: 'The technical sheet could not be attached. Try again.',
    attachForbidden: 'You do not have permission to attach technical sheets.',
    attachNotFound: 'This article-supplier relation no longer exists. Reload the page.',
    attachSuccess: 'Technical sheet attached.',
    attachSaved: 'Technical sheet details saved.',
    errorFileRequired: 'Choose the PDF file of the technical sheet.',
    errorDateRequired: 'Enter the document date.',
    errorDateInvalid: 'The document date is not valid.',
    errorDateFuture: 'The document date cannot be in the future.',
    errorVersionRequired: 'Enter the document version.',
    errorVersionTooLong: 'The version is too long (maximum {{max}} characters).',

    // ── Request ──────────────────────────────────────────────────────────────────────────
    requestTitle: 'Request the technical sheet from {{supplier}}',
    requestIntro:
      'An email will be sent from Obrava to the supplier. You can edit the subject and the text; the list of articles is added automatically.',
    recipientLabel: 'Recipient',
    recipientQuality: '{{email}} (quality email)',
    recipientGeneral: '{{email}} (general email: the supplier has no quality email)',
    noRecipientWarning:
      'This supplier has no email address. Add one on its record (Settings · Suppliers) to be able to request the technical sheet: nothing will be sent.',
    fieldSubject: 'Subject',
    fieldBody: 'Text',
    articlesLabel: 'Articles included ({{count}})',
    articleLine: '{{name}}',
    articleLineRef: '{{name}} · ref. {{ref}}',
    requestConfirm: 'Send the request',
    requestSuccess: 'Request sent to {{recipient}}.',
    errorNoRecipient:
      'This supplier has no email address: nothing has been sent. Add one on its record.',
    errorNoArticles: 'Select at least one article.',
    errorTooManyArticles: 'A request admits at most {{max}} articles.',
    errorSubjectRequired: 'Enter the subject.',
    errorSubjectTooLong: 'The subject is too long (maximum {{max}} characters).',
    errorBodyRequired: 'Enter the message text.',
    errorBodyTooLong: 'The text is too long (maximum {{max}} characters).',
    errorForbidden: 'You do not have permission to request technical sheets.',
    errorSupplierNotFound: 'The supplier was not found.',
    errorArticlesNotLinked:
      'One of the articles is no longer this supplier’s. Close the window, review the list and try again.',
    errorAlreadyHasFt:
      'One of the articles already has its technical sheet attached. Close the window, review the list and try again.',
    errorSmtpNotConfigured: 'Email sending is not configured. Tell the Obrava administrator.',
    errorRegistryUnavailable:
      'The request registry is not available: nothing was sent. Tell the Obrava administrator.',
    errorRecentlyRequested:
      'This supplier was asked for its technical sheet a moment ago: it cannot be written to again for a few minutes.',
    errorDailyLimit:
      'The limit of technical sheet requests for the last 24 hours has been reached. Try again later.',
    limitCooldown:
      'This supplier was asked for its technical sheet a moment ago. So that it does not get repeated emails, it cannot be written to again for {{minutes}} min.',
    limitDaily:
      'The obrador has reached the limit of {{max}} technical sheet requests in 24 hours. It can request again in {{hours}} h.',
    fileBadUrl: 'The file address is not valid.',
    errorSendFailed: 'The email could not be sent. Nothing has been recorded: you can try again.',
    errorRecordFailed:
      'The email was sent, but the request could not be recorded. Do not send it again.',
    errorTryLater: 'The server could not be reached. Try again in a moment.',
    errorGeneric: 'The request could not be sent.',

    // ── The email template (rendered in the supplier's language) ─────────────────────────
    emailSubjectDefault: 'Technical sheet request · {{obrador}}',
    emailBodyDefault:
      'Hello,\n\nTo complete our raw-material records and guarantee their traceability, we need the current technical sheet of the following articles. We would appreciate it if you could reply to this email attaching it as a PDF, with the date and version of the document.\n\nMany thanks.',

    // ── Pending technical sheets by supplier ─────────────────────────────────────────────
    pendingButton: 'Pending tech sheets',
    pendingTitle: 'Pending technical sheets by supplier',
    pendingIntro:
      'The articles that do not have the supplier’s technical sheet yet, grouped by supplier. Each supplier gets a single request with all the articles you tick.',
    pendingTotals: 'Suppliers: {{suppliers}} · Articles without a tech sheet: {{articles}}',
    pendingLoading: 'Loading the pending technical sheets…',
    pendingLoadError: 'The pending technical sheets could not be loaded.',
    pendingEmpty: 'No technical sheet is pending: every article with a supplier has its technical sheet.',
    colArticle: 'Article',
    colRef: 'Supplier ref.',
    colStatus: 'Status',
    groupMeta: 'Without tech sheet: {{count}}',
    groupNoRecipient: 'No email',
    groupNoRecipientHint:
      'This supplier has no email address: add one on its record to be able to request the technical sheet.',
    groupRecipient: 'To: {{recipient}}',
    groupRequestAction: 'Request tech sheet ({{count}})',
    groupSelectNone: 'Tick at least one article to request it.',
    groupSelectAll: 'Tick all the articles of {{supplier}}',
    itemSelect: 'Include {{article}} in the request',
    itemRequestedOn: 'Requested on {{date}}',
    pendingClose: 'Close',
  },
} as const;
